import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp, FileText, X } from "lucide-react";
import generateAvatarUrl from "../../../utils/generateAvatarUrl";
import {
  formatShortCountdown,
  formatVolume,
  getBinaryOdds,
  getH2HLabels,
  getContractLineImageUrl,
  formatChancePct,
  getOutcomeDisplayPrice,
  groupOutcomesBySelection,
  MARKET_TYPES,
  STATUS_META,
} from "../../../utils/bondPredictHelpers";
import {
  buildContractLineMap,
  buildLineResolutionMap,
  effectiveLineMeta,
  isLineTradableNow,
  isYesWinnerForLine,
} from "../../../utils/bondPredictDetailHelpers";
import OutcomeNavItem from "./OutcomeNavItem";

const buildRowHint = (row) => {
  const parts = [];
  if (row.secondaryLabel) parts.push(row.secondaryLabel);
  parts.push(`${formatVolume(row.volume || 0)} vol`);
  if (row.metaLabel) parts.push(row.metaLabel);
  return parts.join(" · ");
};

const LineSection = ({
  card,
  selectedOutcomeId,
  onSelect,
  expanded,
  onToggleExpand,
  collapsed,
}) => (
  <section
    className={`bp-predict-nav-section bp-outcome-nav-line${
      card.groupSelected ? " bp-outcome-nav-line-active" : ""
    }${card.isLineVoid ? " bp-outcome-nav-line-void" : ""}`}
  >
    {!collapsed ? (
      <div className="bp-outcome-nav-line-head">
        <p className="bp-predict-nav-section-label">
          {card.logo ? (
            <img src={card.logo} alt="" className="bp-outcome-nav-line-logo" />
          ) : null}
          <span className="bp-outcome-nav-line-name">{card.baseLabel}</span>
        </p>
        {card.lineMetaLabel ? (
          <span
            className={`bp-outcome-nav-line-status bp-outcome-row-status bp-outcome-row-status-${card.lineMetaTone}`}
          >
            {card.lineMetaLabel}
          </span>
        ) : null}
      </div>
    ) : null}

    {!collapsed && (card.countdown || card.lineVol > 0) ? (
      <p className="bp-outcome-nav-line-meta">
        {card.countdown ? `Closes · ${card.countdown}` : null}
        {card.countdown && card.lineVol > 0 ? " · " : null}
        {card.lineVol > 0 ? `${formatVolume(card.lineVol)} vol` : null}
      </p>
    ) : null}

    <div className="bp-predict-nav-list">
      <OutcomeNavItem
        active={selectedOutcomeId === card.yesOutcome?._id}
        tone="yes"
        label="Yes"
        hint={collapsed ? undefined : buildRowHint(card.yesRow)}
        probability={card.yesRow.probability}
        dimmed={card.yesRow.dimmed}
        winner={card.yesRow.winner}
        collapsed={collapsed}
        onClick={() => onSelect?.(card.yesOutcome)}
      />
      <OutcomeNavItem
        active={selectedOutcomeId === card.noOutcome?._id}
        tone="no"
        label="No"
        hint={collapsed ? undefined : buildRowHint(card.noRow)}
        probability={card.noRow.probability}
        dimmed={card.noRow.dimmed}
        winner={card.noRow.winner}
        collapsed={collapsed}
        onClick={() => onSelect?.(card.noOutcome)}
      />
    </div>

    {!collapsed && card.canExpand ? (
      <>
        <button
          type="button"
          className={`bp-outcome-expand${expanded ? " bp-outcome-expand-open" : ""}`}
          onClick={() => onToggleExpand?.(card.key)}
          aria-expanded={expanded}
        >
          <FileText size={11} />
          <span>{expanded ? "Hide rules" : "Rules & source"}</span>
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        {expanded ? (
          <div className="bp-outcome-expand-panel">
            {card.showLineRules ? (
              <div className="bp-outcome-expand-block">
                <span>Resolution rules</span>
                <p>{card.lineDescription}</p>
              </div>
            ) : null}
            {card.showLineSource ? (
              <div className="bp-outcome-expand-block">
                <span>Resolution source</span>
                <p>{card.displaySource}</p>
              </div>
            ) : null}
          </div>
        ) : null}
      </>
    ) : null}
  </section>
);

const MarketDetailOutcomes = ({
  market,
  selectedOutcomeId,
  onSelectOutcome,
  collapsed = false,
}) => {
  const [expandedLineKeys, setExpandedLineKeys] = useState(() => new Set());
  const [activeProfileKey, setActiveProfileKey] = useState(null);

  const type = market?.type || MARKET_TYPES.BINARY;
  const status = market?.status || "OPEN";
  const outcomes = useMemo(
    () => (Array.isArray(market?.outcomes) ? market.outcomes.filter((o) => o && o._id) : []),
    [market]
  );
  const grouped = useMemo(() => groupOutcomesBySelection(outcomes), [outcomes]);
  const contractLineMap = useMemo(
    () => (type === MARKET_TYPES.MULTI_BINARY ? buildContractLineMap(market) : {}),
    [market, type]
  );
  const lineResolutionMap = useMemo(
    () => (type === MARKET_TYPES.MULTI_BINARY ? buildLineResolutionMap(market) : {}),
    [market, type]
  );

  const toggleExpand = (key) =>
    setExpandedLineKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const pick = (outcome) => {
    if (!outcome?._id) return;
    if (type === MARKET_TYPES.MULTI_BINARY) {
      const group = grouped.find(
        (g) => g.yes?._id === outcome._id || g.no?._id === outcome._id
      );
      if (group?.selectionKey) {
        setExpandedLineKeys((prev) => new Set(prev).add(group.selectionKey));
      }
    }
    onSelectOutcome?.(outcome);
  };

  const flatRows = useMemo(() => {
    if (type === MARKET_TYPES.BINARY) {
      const { yes, no, yesPct, noPct } = getBinaryOdds(market);
      if (!yes || !no) return [];
      return [
        {
          key: yes._id,
          outcome: yes,
          primaryLabel: "Yes",
          probability: yesPct != null ? yesPct / 100 : getOutcomeDisplayPrice(yes),
          volume: yes.metrics?.totalVolume || 0,
          tone: "yes",
        },
        {
          key: no._id,
          outcome: no,
          primaryLabel: "No",
          probability: noPct != null ? noPct / 100 : getOutcomeDisplayPrice(no),
          volume: no.metrics?.totalVolume || 0,
          tone: "no",
        },
      ];
    }

    if (type === MARKET_TYPES.HEAD_TO_HEAD) {
      const h2h = getH2HLabels(market);
      const g = grouped.find((x) => x.yes && x.no);
      if (!h2h || !g) return [];
      return [
        {
          key: g.yes._id,
          outcome: g.yes,
          primaryLabel: h2h.labelA,
          secondaryLabel: h2h.fxStatus || null,
          logo: h2h.logoA || generateAvatarUrl(h2h.labelA),
          probability: getOutcomeDisplayPrice(g.yes),
          volume: g.yes.metrics?.totalVolume || 0,
          tone: "yes",
        },
        {
          key: g.no._id,
          outcome: g.no,
          primaryLabel: h2h.labelB,
          logo: h2h.logoB || generateAvatarUrl(h2h.labelB),
          probability: getOutcomeDisplayPrice(g.no),
          volume: g.no.metrics?.totalVolume || 0,
          tone: "no",
        },
      ];
    }

    return [];
  }, [type, market, grouped]);

  const lines = useMemo(() => grouped.filter((g) => g.yes || g.no), [grouped]);

  const lineCards = useMemo(() => {
    if (type !== MARKET_TYPES.MULTI_BINARY) return [];
    const marketDescription = (market?.description || "").trim();
    const marketSource = (market?.resolutionSource || "").trim();

    return lines.map((g, lineIndex) => {
      const yesOutcome = g.yes;
      const noOutcome = g.no;
      const baseLabel = (yesOutcome?.label || noOutcome?.label || g.selectionKey || "").replace(
        /\s*\((Yes|No)\)\s*$/i,
        ""
      );
      const line = contractLineMap[g.selectionKey];
      const lineRes = lineResolutionMap[g.selectionKey];
      const { status: lineStatus, closeAt } = effectiveLineMeta(market, line, status);
      const lineStatusMeta = STATUS_META[lineStatus] || STATUS_META.OPEN;
      const lineTradable = isLineTradableNow(line, market, status);
      const countdown = formatShortCountdown(closeAt, lineStatus);
      const yesWon = isYesWinnerForLine(g, lineRes);
      const isLineResolved = lineStatus === "RESOLVED" || lineStatus === "FINALIZED";
      const isLineVoid = lineStatus === "VOID" || lineStatus === "REFUNDED";
      const yesPrice = getOutcomeDisplayPrice(yesOutcome);
      const noPrice = getOutcomeDisplayPrice(noOutcome);
      const lineDescription = (line?.description || "").trim();
      const lineSource = Array.isArray(line?.resolutionSources)
        ? line.resolutionSources.find((s) => s && String(s).trim())
        : "";
      const showLineRules = !!lineDescription && lineDescription !== marketDescription;
      const displaySource = lineSource || marketSource;
      const showLineSource = !!displaySource;
      const canExpand = showLineRules || showLineSource;
      const lineVol =
        (yesOutcome?.metrics?.totalVolume || 0) + (noOutcome?.metrics?.totalVolume || 0);
      const groupSelected =
        selectedOutcomeId === yesOutcome?._id || selectedOutcomeId === noOutcome?._id;
      const logo = getContractLineImageUrl(line, baseLabel);

      const lineMetaLabel =
        lineTradable && countdown
          ? countdown
          : isLineResolved && lineRes
          ? yesWon
            ? "Yes won"
            : "No won"
          : isLineVoid
          ? lineStatus === "REFUNDED"
            ? "Refunded"
            : "Voided"
          : lineStatus === "ON_HOLD"
          ? "On hold"
          : lineStatus === "CLOSED"
          ? "Awaiting result"
          : lineStatusMeta.label;

      const lineMetaTone =
        lineTradable && countdown
          ? "live"
          : isLineResolved && lineRes
          ? "win"
          : isLineVoid
          ? "void"
          : lineStatus === "ON_HOLD"
          ? "hold"
          : lineStatus === "CLOSED"
          ? "closed"
          : lineStatus === "RESOLVED"
          ? "pending"
          : lineStatus === "FINALIZED"
          ? "final"
          : "muted";

      return {
        key: g.selectionKey,
        baseLabel,
        logo,
        lineIndex,
        lineCount: lines.length,
        isMultiLine: lines.length > 1,
        countdown,
        closeAt,
        lineVol,
        groupSelected,
        isLineVoid,
        lineMetaLabel,
        lineMetaTone,
        canExpand,
        showLineRules,
        showLineSource,
        lineDescription,
        displaySource,
        yesOutcome,
        noOutcome,
        yesPct: yesPrice != null ? Math.round(yesPrice * 100) : null,
        yesRow: {
          probability: yesPrice,
          volume: yesOutcome?.metrics?.totalVolume || 0,
          metaLabel: null,
          winner: isLineResolved && lineRes && yesWon,
          dimmed: isLineResolved && lineRes && !yesWon,
        },
        noRow: {
          probability: noPrice,
          volume: noOutcome?.metrics?.totalVolume || 0,
          metaLabel: null,
          winner: isLineResolved && lineRes && !yesWon,
          dimmed: isLineResolved && lineRes && yesWon,
        },
      };
    });
  }, [type, market, lines, contractLineMap, lineResolutionMap, status, selectedOutcomeId]);

  const isMultiBinary = type === MARKET_TYPES.MULTI_BINARY;
  const activeCard = useMemo(
    () => (isMultiBinary ? lineCards.find((c) => c.key === activeProfileKey) : null),
    [isMultiBinary, lineCards, activeProfileKey]
  );

  useEffect(() => {
    if (!collapsed && activeProfileKey) setActiveProfileKey(null);
  }, [collapsed, activeProfileKey]);

  useEffect(() => {
    if (activeProfileKey && !lineCards.some((c) => c.key === activeProfileKey)) {
      setActiveProfileKey(null);
    }
  }, [lineCards, activeProfileKey]);

  if (collapsed) {
    if (isMultiBinary) {
      if (!lineCards.length) {
        return <p className="bpd-outcomes-empty">No outcomes</p>;
      }
      return (
        <>
          <div className="bp-predict-nav-list bp-outcome-nav-list-collapsed">
            {lineCards.map((card) => (
              <button
                key={card.key}
                type="button"
                className={`bp-outcome-nav-profile${card.groupSelected ? " bp-outcome-nav-profile-active" : ""}`}
                onClick={() => setActiveProfileKey(card.key)}
                aria-label={`${card.baseLabel} outcomes`}
                title={card.baseLabel}
              >
                <img src={card.logo} alt="" className="bp-outcome-nav-profile-logo" />
                <span className="bp-outcome-nav-profile-pct tabular-nums">
                  {formatChancePct(card.yesRow?.probability)}
                </span>
              </button>
            ))}
          </div>

          {activeCard
            ? createPortal(
                <div
                  className="bpd-outcome-modal-backdrop"
                  role="presentation"
                  onClick={() => setActiveProfileKey(null)}
                >
                  <div
                    className="bpd-outcome-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${activeCard.baseLabel} outcomes`}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <header className="bpd-outcome-modal-head">
                      <img src={activeCard.logo} alt="" className="bpd-outcome-modal-logo" />
                      <div className="bpd-outcome-modal-head-copy">
                        <strong>{activeCard.baseLabel}</strong>
                      </div>
                      <button
                        type="button"
                        className="bpd-outcome-modal-close"
                        onClick={() => setActiveProfileKey(null)}
                        aria-label="Close outcomes"
                      >
                        <X size={16} />
                      </button>
                    </header>
                    <div className="bpd-outcome-modal-body">
                      <LineSection
                        card={activeCard}
                        selectedOutcomeId={selectedOutcomeId}
                        onSelect={(outcome) => {
                          pick(outcome);
                          setActiveProfileKey(null);
                        }}
                        expanded={expandedLineKeys.has(activeCard.key)}
                        onToggleExpand={toggleExpand}
                        collapsed={false}
                      />
                    </div>
                  </div>
                </div>,
                document.body
              )
            : null}
        </>
      );
    }

    if (!flatRows.length) return <p className="bpd-outcomes-empty">No outcomes</p>;

    return (
      <div className="bp-predict-nav-list bp-outcome-nav-list-collapsed">
        {flatRows.map((row) => (
          <OutcomeNavItem
            key={row.key}
            active={selectedOutcomeId === row.outcome._id}
            tone={row.tone}
            label={row.primaryLabel}
            logo={row.logo}
            probability={row.probability}
            collapsed
            onClick={() => pick(row.outcome)}
          />
        ))}
      </div>
    );
  }

  if (type === MARKET_TYPES.BINARY || type === MARKET_TYPES.HEAD_TO_HEAD) {
    if (!flatRows.length) return <p className="bpd-outcomes-empty">No outcomes</p>;
    return (
      <div className="bp-predict-nav-list">
        {flatRows.map((row) => (
          <OutcomeNavItem
            key={row.key}
            active={selectedOutcomeId === row.outcome._id}
            tone={row.tone}
            label={row.primaryLabel}
            logo={row.logo}
            hint={buildRowHint(row)}
            probability={row.probability}
            onClick={() => pick(row.outcome)}
          />
        ))}
      </div>
    );
  }

  if (!lineCards.length) return <p className="bpd-outcomes-empty">No outcomes</p>;

  return (
    <div className="bp-outcome-nav-lines">
      {lineCards.map((card) => (
        <LineSection
          key={card.key}
          card={card}
          selectedOutcomeId={selectedOutcomeId}
          onSelect={pick}
          expanded={expandedLineKeys.has(card.key)}
          onToggleExpand={toggleExpand}
          collapsed={false}
        />
      ))}
    </div>
  );
};

export default MarketDetailOutcomes;
